import React, { useState, useEffect } from 'react';
import {
  TrendingUp, Clock, CheckCircle2, AlertCircle, Zap, Calendar, MapPin,
  BarChart3, Wrench, Droplets, Target, Award, AlertTriangle,
  ArrowUpRight, History, Timer
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { getAuthHeader } from '../../helpers/auth';

// ─── ADMIN THEME TOKENS ──────────────────────────────────────────
const T = {
  bg:       '#080C18',
  surface:  '#0D1120',
  border:   'rgba(255,255,255,0.06)',
  text:     '#E2E8F0',
  muted:    'rgba(255,255,255,0.35)',
  accent:   '#3B82F6', // Blue
  success:  '#22D3A5', // Green
  warning:  '#F59E0B', // Amber
  danger:   '#F87171', // Red
};

const CustomersPortal = () => {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [topSites, setTopSites] = useState([]);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [alertsData, setAlertsData] = useState([]);
  const [timeStats, setTimeStats] = useState({ avgStart: 'N/A', avgReached: 'N/A', avgDuration: 'N/A' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) setUser(JSON.parse(userStr));
    } catch (err) { console.error('Error loading user:', err); }
    fetchDashboardData();
  }, []);

  // --- Calculations (Keeping your original logic) ---
  const calculateAverageTimeOfDay = (jobs, dateField, fallbackField = null) => {
    const times = jobs.map(j => j[dateField] || j[fallbackField]).filter(val => val)
      .map(dateStr => { const d = new Date(dateStr); return d.getHours() * 60 + d.getMinutes(); });
    if (times.length === 0) return 'N/A';
    const avg = times.reduce((acc, curr) => acc + curr, 0) / times.length;
    const h = Math.floor(avg / 60); const m = Math.round(avg % 60);
    return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
  };

  const calculateAverageDuration = (jobs) => {
    const durations = jobs.filter(j => j.started_at && j.completed_at)
      .map(j => new Date(j.completed_at) - new Date(j.started_at));
    if (durations.length === 0) return 'N/A';
    const avgMs = durations.reduce((acc, curr) => acc + curr, 0) / durations.length;
    const totalMins = Math.floor(avgMs / (1000 * 60));
    const h = Math.floor(totalMins / 60); const m = totalMins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const config = getAuthHeader();
      const [commRes, maintRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/commercial-jobs-history/history`, { ...config, params: { dateRange: 'all' } }),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/commercial-jobs-history/maintenance-history`, { ...config, params: { dateRange: 'all' } })
      ]);

      const allJobs = [
        ...(commRes.data.jobs || []).map(j => ({ ...j, job_type: 'commercial' })),
        ...(maintRes.data.jobs || []).map(j => ({ ...j, job_type: 'maintenance' }))
      ];

      setTimeStats({
        avgStart: calculateAverageTimeOfDay(allJobs, 'started_at'),
        avgReached: calculateAverageTimeOfDay(allJobs, 'reached_at', 'before_photos_at'),
        avgDuration: calculateAverageDuration(allJobs)
      });

      const dStats = {
        totalJobs: allJobs.length,
        completed: allJobs.filter(j => j.status === 'completed').length,
        inProgress: allJobs.filter(j => ['started', 'in_progress', 'before_photos', 'after_photos'].includes(j.status)).length,
        scheduled: allJobs.filter(j => j.status === 'scheduled').length,
        commercial: allJobs.filter(j => j.job_type === 'commercial').length,
        maintenance: allJobs.filter(j => j.job_type === 'maintenance').length,
      };

      setPerformanceMetrics({
        onTimeCompletion: Math.round((dStats.completed / Math.max(dStats.totalJobs, 1)) * 100),
      });

      const siteCounts = {};
      allJobs.forEach(j => { siteCounts[j.site_name] = (siteCounts[j.site_name] || 0) + 1; });
      setTopSites(Object.entries(siteCounts).sort((a,b) => b[1]-a[1]).slice(0,5).map(([site, count]) => ({ site, count })));

      const monthlyData = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date(); d.setMonth(d.getMonth() - i);
        const mJobs = allJobs.filter(j => new Date(j.scheduled_date).getMonth() === d.getMonth());
        monthlyData.push({ month: format(d, 'MMM'), completed: mJobs.filter(j => j.status === 'completed').length, pending: mJobs.filter(j => j.status !== 'completed').length, total: mJobs.length });
      }
      setTimeSeriesData(monthlyData);
      setStats(dStats);

      // Alerts logic...
      const alerts = [];
      if (dStats.inProgress > 0) alerts.push({ id: 1, type: 'warning', title: 'Active Services', message: `Currently ${dStats.inProgress} jobs in progress.`, icon: AlertTriangle });
      setAlertsData(alerts);

    } catch (err) { setError('Failed to load dashboard data.'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');
        @keyframes slideIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .card { animation: slideIn 0.4s ease-out both; background: ${T.surface}; border: 1px solid ${T.border}; border-radius: 16px; transition: all 0.3s ease; }
        .card:hover { transform: translateY(-5px); box-shadow: 0 12px 30px rgba(0,0,0,0.4); border-color: rgba(255,255,255,0.12); }
        .mono { font-family: 'DM Mono', monospace; }
      `}</style>

      <div style={{ flex: 1, padding: '32px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <div style={{ width: '40px', height: '40px', border: '3px solid rgba(255,255,255,0.05)', borderTopColor: T.accent, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 20px' }} />
            <p style={{ color: T.muted, fontSize: '14px', letterSpacing: '0.1em' }}>INITIALIZING PORTAL...</p>
          </div>
        ) : error ? (
          <div style={{ background: 'rgba(248,113,113,0.05)', border: `1px solid ${T.danger}`, borderRadius: '16px', padding: '32px', textAlign: 'center' }}>
            <AlertCircle style={{ width: '48px', height: '48px', color: T.danger, margin: '0 auto 16px' }} />
            <p style={{ color: T.text }}>{error}</p>
            <button onClick={fetchDashboardData} style={{ marginTop: '16px', padding: '10px 20px', background: T.danger, color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>Retry</button>
          </div>
        ) : (
          <>
            {/* Alerts */}
            {alertsData.length > 0 && (
              <div style={{ marginBottom: '32px', display: 'grid', gap: '12px' }}>
                {alertsData.map(alert => (
                  <div key={alert.id} style={{ background: 'rgba(245,158,11,0.05)', border: `1px solid rgba(245,158,11,0.2)`, borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <alert.icon style={{ color: T.warning, width: '20px', height: '20px' }} />
                    <div>
                      <div style={{ color: T.warning, fontWeight: '700', fontSize: '14px' }}>{alert.title}</div>
                      <div style={{ color: T.muted, fontSize: '12px' }}>{alert.message}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* KPI Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '32px' }}>
              <div className="card" style={{ padding: '24px', borderTop: `3px solid ${T.accent}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <span style={{ fontSize: '10px', fontWeight: '700', color: T.muted, letterSpacing: '0.1em' }}>TOTAL JOBS</span>
                  <BarChart3 size={20} color={T.accent} />
                </div>
                <div className="mono" style={{ fontSize: '32px', fontWeight: '800' }}>{stats?.totalJobs || 0}</div>
              </div>

              <div className="card" style={{ padding: '24px', borderTop: `3px solid ${T.success}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <span style={{ fontSize: '10px', fontWeight: '700', color: T.muted, letterSpacing: '0.1em' }}>COMPLETED</span>
                  <CheckCircle2 size={20} color={T.success} />
                </div>
                <div className="mono" style={{ fontSize: '32px', fontWeight: '800' }}>{stats?.completed || 0}</div>
              </div>

              <div className="card" style={{ padding: '24px', borderTop: `3px solid ${T.warning}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <span style={{ fontSize: '10px', fontWeight: '700', color: T.muted, letterSpacing: '0.1em' }}>SUCCESS RATE</span>
                  <Target size={20} color={T.warning} />
                </div>
                <div className="mono" style={{ fontSize: '32px', fontWeight: '800' }}>{performanceMetrics?.onTimeCompletion || 0}%</div>
              </div>
            </div>

            {/* Charts Area */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '32px' }}>
              <div className="card" style={{ padding: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: T.muted, marginBottom: '24px', letterSpacing: '0.05em' }}>SERVICE TREND (6 MONTHS)</h3>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '180px' }}>
                  {timeSeriesData.map((data, idx) => (
                    <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ position: 'relative', width: '100%', height: '140px', background: 'rgba(255,255,255,0.02)', borderRadius: '4px', overflow: 'hidden', display: 'flex', alignItems: 'flex-end' }}>
                        <div style={{ width: '100%', height: `${(data.completed / (Math.max(...timeSeriesData.map(d => d.total)) || 1)) * 100}%`, background: `linear-gradient(to top, ${T.success}80, ${T.success})` }} />
                      </div>
                      <span className="mono" style={{ fontSize: '10px', textAlign: 'center', color: T.muted }}>{data.month}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card" style={{ padding: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: T.muted, marginBottom: '24px', letterSpacing: '0.05em' }}>TOP LOCATIONS</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {topSites.map((site, idx) => (
                    <div key={idx}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                        <span style={{ fontWeight: '600' }}>{site.site}</span>
                        <span className="mono" style={{ color: T.accent }}>{site.count}</span>
                      </div>
                      <div style={{ width: '100%', height: '4px', background: 'rgba(255,255,255,0.05)', borderRadius: '2px' }}>
                        <div style={{ width: `${(site.count / topSites[0].count) * 100}%`, height: '100%', background: T.accent, borderRadius: '2px' }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
              <div className="card" style={{ padding: '24px', borderLeft: `4px solid ${T.accent}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <Droplets size={24} color={T.accent} />
                  <span style={{ fontWeight: '700' }}>COMMERCIAL SOILING</span>
                </div>
                <div className="mono" style={{ fontSize: '28px', fontWeight: '800' }}>{stats?.commercial || 0}</div>
                <p style={{ fontSize: '11px', color: T.muted, marginTop: '8px' }}>Facility cleaning & solar soiling</p>
              </div>

              <div className="card" style={{ padding: '24px', borderLeft: `4px solid ${T.success}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <Wrench size={24} color={T.success} />
                  <span style={{ fontWeight: '700' }}>MAINTENANCE</span>
                </div>
                <div className="mono" style={{ fontSize: '28px', fontWeight: '800' }}>{stats?.maintenance || 0}</div>
                <p style={{ fontSize: '11px', color: T.muted, marginTop: '8px' }}>System health & repairs</p>
              </div>

              <div className="card" style={{ padding: '24px', background: 'rgba(255,255,255,0.02)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                  <Award size={20} color={T.warning} />
                  <span style={{ fontWeight: '700', fontSize: '14px' }}>QUICK STATS</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                    <span style={{ fontSize: '11px', color: T.muted }}>Avg Start Time</span>
                    <span className="mono" style={{ fontSize: '13px' }}>{timeStats.avgStart}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                    <span style={{ fontSize: '11px', color: T.muted }}>Avg Duration</span>
                    <span className="mono" style={{ fontSize: '13px' }}>{timeStats.avgDuration}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomersPortal;